import { useState, type ReactNode } from 'react';
import { KeyRound } from 'lucide-react';
import { Button, Card, cn } from '@eombor/ui';
import { ApiClientError } from '../../../shared/api/client.js';
import { t } from '../../../shared/i18n/index.js';
import { PasswordInput } from '../../../shared/ui/PasswordInput.js';
import { authApi, useAuthStore } from '../model/auth.store.js';

/**
 * MAJBURIY parol almashtirish.
 *
 * Vaqtinchalik parol Telegram yozishmasida qoladi va uni administrator ham
 * bilishi mumkin — ya'ni u parol emas, bir martalik kalit. Shu sababli
 * `mustChangePassword` bayrog'i ko'tarilgan xodim tizimning boshqa hech
 * qaysi ekraniga o'ta olmaydi.
 *
 * To'siq bu yerda, ilova QOBIG'IDA turadi — har bir sahifada emas: bitta
 * sahifa unutilsa, majburiy almashtirish jimgina chetlab o'tilardi.
 * Haqiqiy himoya baribir serverda: eski parol tekshiriladi va parol
 * almashgach barcha seanslar yopiladi.
 */

/** Ekranda ko'rsatiladigan talablar — `validatePasswordStrength` bilan bir xil. */
const RULES = ['length', 'digit', 'upper', 'lower'] as const;

const meets = (password: string, rule: (typeof RULES)[number]): boolean => {
  if (rule === 'length') return password.length >= 10;
  if (rule === 'digit') return /\d/.test(password);
  if (rule === 'upper') return /[A-Z]/.test(password);
  return /[a-z]/.test(password);
};

export const ChangePasswordGate = ({ children }: { children: ReactNode }) => {
  const user = useAuthStore((s) => s.user);
  const setUser = useAuthStore((s) => s.setUser);
  /*
   * Kirishda terilgan kalit — maydonga o'zi yoziladi. Odam uni bir daqiqa
   * oldin Telegramdan olib terdi va ikkinchi marta terishi mumkin emas:
   * `useState` boshlang'ich qiymati faqat birinchi chizishda o'qiladi,
   * shuning uchun keyin uni tahrirlashga hech narsa xalaqit bermaydi.
   */
  const pending = useAuthStore((s) => s.pendingPassword);

  const [current, setCurrent] = useState(pending ?? '');
  const [next, setNext] = useState('');
  const [repeat, setRepeat] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  if (user === null || !user.mustChangePassword) return <>{children}</>;

  const unmet = RULES.filter((rule) => !meets(next, rule));
  const mismatch = repeat.length > 0 && next !== repeat;
  const ready = current.length > 0 && unmet.length === 0 && !mismatch && repeat.length > 0;

  const submit = (): void => {
    setError(null);
    setSaving(true);
    authApi
      .changePassword(current, next)
      .then(() => {
        /*
         * Seanslar serverda yopildi — shu jumladan HOZIRGISI ham. Foydalanuvchini
         * qaytadan kirishga yuborish o'rniga bayroqni tushiramiz va ilovaga
         * kiritamiz: `access token` o'z muddatigacha ishlaydi, muddati
         * tugagach esa yangilash yiqilib, u baribir kirish sahifasiga tushadi.
         * Ikkalasining orasida u ishini davom ettira oladi.
         */
        setUser({ ...user, mustChangePassword: false });
      })
      .catch((cause: unknown) => {
        setError(cause instanceof ApiClientError ? cause.message : t('password.failed'));
      })
      .finally(() => setSaving(false));
  };

  return (
    <div className="bg-bg flex min-h-dvh items-center justify-center px-4 py-10">
      <Card className="w-full max-w-[28rem]">
        <div className="flex flex-col gap-5">
          <div className="flex items-start gap-3">
            <span
              aria-hidden
              className="bg-accent-soft text-accent flex size-10 shrink-0 items-center justify-center rounded-[var(--radius-sm)]"
            >
              <KeyRound size={18} />
            </span>
            <div className="min-w-0">
              <h1 className="text-lg font-semibold tracking-tight">{t('password.title')}</h1>
              <p className="text-text-muted mt-1 text-sm text-pretty">{t('password.subtitle')}</p>
            </div>
          </div>

          {error === null ? null : (
            <p
              role="alert"
              className="border-danger/30 bg-danger-soft text-danger rounded-[var(--radius-sm)] border px-3 py-2 text-sm"
            >
              {error}
            </p>
          )}

          <div className="flex flex-col gap-4">
            <PasswordInput
              label={t('password.current')}
              autoComplete="current-password"
              autoFocus
              value={current}
              onChange={(event) => setCurrent(event.target.value)}
            />
            <PasswordInput
              label={t('password.next')}
              autoComplete="new-password"
              value={next}
              onChange={(event) => setNext(event.target.value)}
            />
            <PasswordInput
              label={t('password.repeat')}
              autoComplete="new-password"
              value={repeat}
              onChange={(event) => setRepeat(event.target.value)}
              {...(mismatch ? { error: t('password.mismatch') } : {})}
            />
          </div>

          {/* Talablar RO'YXAT bo'lib turadi va bajarilgani belgilanadi: bitta
              umumiy "parol kuchsiz" xabari odamni taxmin qilishga majburlaydi. */}
          <ul className="flex flex-col gap-1 text-xs">
            {RULES.map((rule) => {
              const ok = meets(next, rule);
              return (
                <li
                  key={rule}
                  className={cn('flex items-center gap-2', ok ? 'text-accent' : 'text-text-faint')}
                >
                  <span aria-hidden>{ok ? '✓' : '•'}</span>
                  {t(`password.rule.${rule}`)}
                </li>
              );
            })}
          </ul>

          <Button size="lg" loading={saving} disabled={!ready} onClick={submit}>
            {t('password.submit')}
          </Button>
        </div>
      </Card>
    </div>
  );
};
